import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useCouncilStore, Council, CouncilMember } from '../../store/useCouncilStore';

export default function CouncilPublicPage() {
  const navigate = useNavigate();
  const getCouncils = useCouncilStore((state) => state.getCouncils);
  const councils = useCouncilStore((state) => state.councils);
  const loading = useCouncilStore((state) => state.loading);

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    // Fetch all councils (public endpoint)
    getCouncils(true, undefined, 1, 1000); // Fetch all councils
  }, [getCouncils]);

  // Sort councils by createdAt descending (newest first)
  const sortedCouncils = React.useMemo(() => {
    return [...councils].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  }, [councils]);

  useEffect(() => {
    // Reset to first council when councils are loaded
    if (sortedCouncils.length > 0) {
      setCurrentIndex(0);
    }
  }, [sortedCouncils.length]);

  const currentCouncil: Council | null = sortedCouncils[currentIndex] || null;

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < sortedCouncils.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B8860B] mx-auto mb-4"></div>
          <p className="text-gray-600" style={{ fontFamily: 'Lato, sans-serif' }}>
            Loading council information...
          </p>
        </div>
      </div>
    );
  }

  if (sortedCouncils.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-3xl font-bold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            No Council Information Available
          </h1>
          <p className="text-gray-600 mb-8" style={{ fontFamily: 'Lato, sans-serif' }}>
            Council information will be available soon.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium"
            style={{ fontFamily: 'Lato, sans-serif' }}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-700 hover:text-[#B8860B] transition-colors"
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Home
            </button>
            <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Playfair Display, serif' }}>
              Church Council
            </h1>
            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-5xl mx-auto">
          {/* Navigation Arrows and Counter */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                currentIndex === 0
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-[#B8860B] hover:text-white shadow-md'
              }`}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            <div className="text-center">
              <span className="text-gray-600 font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                {currentIndex + 1} of {sortedCouncils.length}
              </span>
              <div className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                {currentCouncil?.period}
              </div>
            </div>

            <button
              onClick={handleNext}
              disabled={currentIndex === sortedCouncils.length - 1}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                currentIndex === sortedCouncils.length - 1
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-white text-gray-700 hover:bg-[#B8860B] hover:text-white shadow-md'
              }`}
              style={{ fontFamily: 'Lato, sans-serif' }}
            >
              Next
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Council Card */}
          <div className="bg-white rounded-lg shadow-xl overflow-hidden">
            {/* Image */}
            {currentCouncil.image ? (
              <div className="w-full h-96 overflow-hidden">
                <img src={currentCouncil.image} alt={`Council ${currentCouncil.period}`} className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="w-full h-64 bg-gradient-to-br from-[#B8860B] to-[#9A7209] flex items-center justify-center">
                <span className="text-white text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Council {currentCouncil.period}
                </span>
              </div>
            )}

            <div className="p-8">
              {/* Period */}
              <div className="flex items-center gap-2 text-[#B8860B] mb-6">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xl font-semibold" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {currentCouncil.period}
                </span>
              </div>

              {/* Description */}
              {currentCouncil.description && (
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    About This Council
                  </h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line" style={{ fontFamily: 'Lato, sans-serif' }}>
                    {currentCouncil.description}
                  </p>
                </div>
              )}

              {/* Members Section */}
              <div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Council Members ({currentCouncil.members?.length || 0})
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentCouncil.members && currentCouncil.members.length > 0 ? (
                    currentCouncil.members.map((member, index) => {
                      // Handle new format: { member: {...}, name: "...", position: "..." }
                      let memberData: CouncilMember | null = null;
                      let memberName = 'Unknown Member';
                      let memberId = '';
                      let position = '';
                      let isManual = false;

                      if (typeof member === 'object' && ('member' in member || 'name' in member)) {
                        // New format with position
                        const memberObj = member as { member?: string | CouncilMember; name?: string; position?: string };
                        
                        // Check if it's a manual member (has name but no member)
                        if (memberObj.name && !memberObj.member) {
                          isManual = true;
                          memberName = memberObj.name;
                          position = memberObj.position || '';
                        } else if (memberObj.member) {
                          // System member
                          memberData = typeof memberObj.member === 'object' && 'name' in memberObj.member ? memberObj.member : null;
                          memberName = memberData ? memberData.name : 'Unknown Member';
                          memberId = typeof memberObj.member === 'string' ? memberObj.member : (memberData?._id || '');
                          position = memberObj.position || '';
                        }
                      } else if (typeof member === 'object' && 'name' in member && '_id' in member) {
                        // Old format: just member object (CouncilMember)
                        memberData = member as CouncilMember;
                        memberName = memberData.name;
                        memberId = memberData._id || '';
                      } else if (typeof member === 'string') {
                        // Old format: just member ID
                        memberId = member;
                      }

                      return (
                        <div key={isManual ? `manual-${memberName}-${index}` : memberId || index} className="bg-gray-50 rounded-lg p-6 hover:shadow-md transition-shadow border border-gray-200">
                          <div className="flex items-start gap-4">
                            <div className="w-16 h-16 bg-gradient-to-br from-[#B8860B] to-[#9A7209] rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                                {memberName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-800 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                                {memberName}
                              </h3>
                              <p className="text-sm text-[#B8860B] font-medium" style={{ fontFamily: 'Lato, sans-serif' }}>
                                {position || 'Member'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="col-span-full text-center text-gray-500 py-8" style={{ fontFamily: 'Lato, sans-serif' }}>
                      No members information available
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
